import 'react';
import { createChainedFunction } from '@atc/create-chained-function';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { sendClick } from 'reaxl-analytics';
import { editDealerOfferCTAClick, telMetricsPhoneTextClick, contactDealerClick, creditAppFollowupClick, creditAppGetPreQualifiedClick, textLinkDealClick } from 'reaxl-analytics-handlers';
import { useFeatures } from '@atc/features';
import { LoadingSpinner, Button, Panel, Label, Link } from 'reaxl';
import { useWalletCalcConfig, WALLET_EMAIL_DEALER_REQUEST, CLOSE_WALLET, WALLET_UPDATE_DEAL_NOTIFICATION_REQUEST } from 'wallet-manager';
import { eventBus } from '@atc/cai-event-bus';
import _isEmpty from 'lodash/isEmpty';
import DealCardVehicleOverview from './DealCardVehicleOverview.js';
import DealCardOfferDetails from './DealCardOfferDetails.js';
import DealCardVehicleProtection from './DealCardVehicleProtection.js';
import DealCardDeepLink from './DealCardDeepLink.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
const pipe = /*#__PURE__*/jsx("span", {
  className: "padding-horizontal-2",
  children: "|"
});
const testDriveDeeplink = (vdpUrl, onScheduleTestDriveClick, label) => /*#__PURE__*/jsx(Link, {
  href: vdpUrl,
  onClick: event => onScheduleTestDriveClick(event, label),
  label: label,
  undecorated: true
});
const getTestDriveStatus = _ref => {
  let {
    testDriveCompleted,
    scheduledDate
  } = _ref;
  if (testDriveCompleted) {
    return 'completed';
  }
  if (scheduledDate) {
    return 'submitted';
  }
  return null;
};

// Ensure status after submitted
const getCreditDecisionStatus = _ref2 => {
  let {
    dealerDecisionStatus = '',
    creditAppSubmittedFlag = false
  } = _ref2;
  if (dealerDecisionStatus) {
    // some credit decisions come in as camel case and we add a space using the split/regex/join combo
    return dealerDecisionStatus === 'Declined' ? 'Needs Attention' : dealerDecisionStatus?.split(/(?=[A-Z])/).join(' ');
  }
  if (creditAppSubmittedFlag) {
    return 'Submitted';
  }
  return null;
};
const tableData = {
  creditPreApproval: {
    label: 'Credit Pre-Approval',
    noStatus: props => /*#__PURE__*/jsx(DealCardDeepLink, {
      ...props,
      buttonText: "Apply for Financing",
      route: "credit"
    }),
    postStatus: (props, onEmailDealerClick) => /*#__PURE__*/jsx(Button, {
      bsStyle: "text",
      onClick: event => onEmailDealerClick(event, 'walletCreditApp'),
      children: "Follow-up"
    })
  },
  sacaApplication: {
    label: 'Credit Pre-Approval',
    noStatus: _ref3 => {
      let {
        handleSACAApplicationClick,
        isVdp,
        ...rest
      } = _ref3;
      return /*#__PURE__*/jsx(Button, {
        bsStyle: "text",
        onClick: () => handleSACAApplicationClick(),
        children: "Apply for Financing"
      });
    },
    postStatus: _ref4 => {
      let {
        handleSACAFollowUpClick,
        ...rest
      } = _ref4;
      return /*#__PURE__*/jsx(Button, {
        bsStyle: "text",
        onClick: () => handleSACAFollowUpClick(),
        children: "Follow-up"
      });
    }
  },
  testDrive: {
    label: 'Test Drive',
    noStatus: _ref5 => {
      let {
        isAccelerate,
        vdpUrl,
        onScheduleTestDriveClick,
        ...rest
      } = _ref5;
      return /*#__PURE__*/jsxs(Fragment, {
        children: [isAccelerate && /*#__PURE__*/jsx(DealCardDeepLink, {
          ...rest,
          buttonText: "Schedule",
          route: "testDrive"
        }), !isAccelerate && testDriveDeeplink(vdpUrl, onScheduleTestDriveClick, 'Schedule')]
      });
    },
    postStatus: _ref6 => {
      let {
        testDriveStatus,
        onTestDriveMarkDoneClick,
        isAccelerate,
        vdpUrl,
        onScheduleTestDriveClick,
        ...rest
      } = _ref6;
      const hasBeenCompleted = testDriveStatus === 'completed';
      const buttonText = hasBeenCompleted ? 'Schedule Another Visit' : 'Reschedule';
      return /*#__PURE__*/jsxs(Fragment, {
        children: [!hasBeenCompleted && /*#__PURE__*/jsxs(Fragment, {
          children: [/*#__PURE__*/jsx(Button, {
            bsStyle: "text",
            onClick: onTestDriveMarkDoneClick,
            children: "Mark as done"
          }), pipe]
        }), isAccelerate && /*#__PURE__*/jsx(DealCardDeepLink, {
          ...rest,
          buttonText: buttonText,
          route: "testDrive"
        }), !isAccelerate && testDriveDeeplink(vdpUrl, onScheduleTestDriveClick, buttonText)]
      });
    }
  }
};
const renderTableRow = function (key, status, props, onEmailDealerClick, vehicleProtectionProgram) {
  let isReceiptView = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : false;
  // 1. hide <hr /> when isReceiptView is true
  // 2. always show <hr /> after creditPreApproval, since testDrive always exists
  // 3. show <hr /> after testDrive when vehicleProtectionProgram shows up
  const showHrElement = !isReceiptView && (key === 'creditPreApproval' || key === 'sacaApplication' || key === 'testDrive' && vehicleProtectionProgram && props.isDr);
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsxs("div", {
      className: clsx({
        'margin-bottom-2': isReceiptView && key === 'creditPreApproval' || key === 'sacaApplication'
      }, 'display-flex justify-content-between'),
      children: [/*#__PURE__*/jsxs("div", {
        className: "display-flex align-items-center",
        children: [/*#__PURE__*/jsx("div", {
          className: "text-bold",
          children: tableData[key]?.label
        }), status && /*#__PURE__*/jsx(Label, {
          "data-cmp": "dealStatus",
          bsStyle: "default",
          className: "margin-horizontal-2",
          children: status
        })]
      }), /*#__PURE__*/jsx("div", {
        className: "text-right",
        "data-cmp": `deal-${key}`,
        children: status ? tableData[key]?.postStatus(props, onEmailDealerClick) : tableData[key]?.noStatus(props)
      })]
    }), showHrElement && /*#__PURE__*/jsx("hr", {})]
  });
};
function PanelWrapper(_ref7) {
  let {
    children,
    isReceiptView
  } = _ref7;
  return !isReceiptView ? /*#__PURE__*/jsx(Panel, {
    "data-cmp": "dealCard",
    children: /*#__PURE__*/jsx(Panel.Body, {
      children: children
    })
  }) : /*#__PURE__*/jsx("div", {
    "data-cmp": "dealCard",
    children: children
  });
}
function DealCard(_ref8) {
  let {
    dealData = {},
    handleActiveWalletPanel,
    onEmailCTAClick = () => {},
    indexClicked,
    paymentsDuck,
    dealsDuck,
    uiContext,
    addListingToHideReceipt = () => {},
    onSACAApplicationClick,
    enableSaca,
    pageName,
    isLoading
  } = _ref8;
  const {
    active_deals_my_wallet: [, {
      event_bus_contact_email: eventBusContactEmail
    }]
  } = useFeatures(['active_deals_my_wallet']);
  const isReceiptView = uiContext === 'receipt';
  const dispatch = useDispatch();
  const paymentsInfo = useSelector(paymentsDuck.selectors.getDuckState);
  const {
    id: dealId,
    isEcommerce = false,
    isAccelerate,
    vehicle = {},
    deal = {},
    creditApp = {},
    tradeIn,
    testDrive = {},
    dealer = {}
  } = dealData;
  const {
    id,
    vdpUrl,
    vdpBaseUrl,
    esntial = false,
    vehicleProtectionProgram
  } = vehicle;
  const testDriveStatus = getTestDriveStatus(testDrive);
  const creditDecisionStatus = getCreditDecisionStatus(creditApp);
  const isCashDeal = deal?.dealType === 'Cash';
  const isVdp = pageName === 'vdp';
  const creditAppDeals = useSelector(state => dealsDuck?.selectors?.getCreditAppDealsByOwnerId?.(state, dealer?.id)) || [];
  const isOwnerFinancingSubmitted = !_isEmpty(creditAppDeals);
  const isEsntialDeal = esntial && !esntial?.deemphasized && !isAccelerate && !isEcommerce;
  const onSDPError = () => {
    // Fall back to wallet lead form if sdp errors out
    handleActiveWalletPanel({
      panel: 'emailForm',
      activeDealId: dealId,
      uiContext: 'wallet'
    });
  };
  const onTestDriveMarkDoneClick = event => {
    eventBus.emit(WALLET_UPDATE_DEAL_NOTIFICATION_REQUEST, {
      dealId,
      body: {
        testDriveCompleted: true
      }
    });
    dispatch(dealsDuck?.creators?.updateDealReminders({
      dealId,
      testDriveCompleted: true
    }));
    sendClick(textLinkDealClick, event, {
      inventoryId: listing?.id,
      text: 'Mark as done'
    });
  };
  const onEmailDealerClick = (event, emailUiContext) => {
    event.stopPropagation();
    if (eventBusContactEmail) {
      eventBus.emit(WALLET_EMAIL_DEALER_REQUEST, {
        dealId,
        vehicle,
        vdpUrl
      });
      eventBus.emit(CLOSE_WALLET);
    } else {
      handleActiveWalletPanel({
        panel: 'emailForm',
        activeDealId: dealId,
        uiContext: emailUiContext
      });
    }
    sendClick(contactDealerClick, event, {
      inventoryId: id
    });
  };
  const onPhoneAndTextClick = leadType => phoneNumber => {
    const data = {
      dealXg: dealData,
      localStorageDeal: {
        testDriveStatus
      },
      leadType,
      listing: vehicle,
      phoneNumber
    };
    sendClick(telMetricsPhoneTextClick, {}, data);
  };
  const handleWalletModalClose = () => {
    dispatch(paymentsDuck.creators.updateMyWallet({
      displayModal: false
    }));
  };
  const onScheduleTestDriveClick = (event, buttonText) => {
    handleWalletModalClose();
    sendClick(textLinkDealClick, event, {
      inventoryId: id,
      text: buttonText,
      isTestDrive: true
    });
  };
  const getVDPUrl = function (listingId) {
    let query = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
    // NOTE: VDP Url could come from the vin validation process in syndicated sites (DDC sites for example)
    if (vdpUrl) {
      return vdpUrl;
    }
    return vdpBaseUrl ? `${vdpBaseUrl}${query}#${listingId}` : '';
  };
  const handleSACAApplicationClick = async event => {
    sendClick(creditAppGetPreQualifiedClick, event, {
      listingId: id,
      partner: 'saca'
    });
    if (!isVdp) {
      window.open(getVDPUrl('nativeDealContainer', '&launchCreditApp=true'), '_self');
    }
    onSACAApplicationClick();
    handleWalletModalClose();
  };
  const handleSACAFollowUpClick = event => {
    sendClick(creditAppFollowupClick, event, {
      inventoryId: id,
      text: 'Follow-up'
    });
    handleActiveWalletPanel({
      panel: 'emailForm',
      activeDealId: dealId,
      uiContext: 'walletCreditApp'
    });
  };
  const {
    enableNegotiation
  } = useWalletCalcConfig(paymentsInfo, vehicle);
  const dealDataWithDealer = {
    ...dealData,
    dealer,
    vehicle: {
      ...dealData.vehicle,
      negotiation: enableNegotiation,
      owner: dealer,
      // SDP requires owner data to update dr deals
      vdpUrl: vdpUrl || vdpBaseUrl
    }
  };
  const linkProps = {
    ...dealDataWithDealer,
    paymentsDuck,
    dealsDuck,
    onSDPError,
    paymentsInfo,
    isReceiptView,
    dealId
  };
  const sharedProps = {
    dealData: dealDataWithDealer,
    paymentsDuck,
    dealsDuck,
    onSDPError,
    paymentsInfo,
    listingId: id,
    isReceiptView
  };
  const handleDealReceiptOverride = () => {
    addListingToHideReceipt(true);
  };
  const fireEditDealerOfferCTAClick = event => {
    sendClick(editDealerOfferCTAClick, event, {
      inventoryId: id,
      isReceiptView
    });
  };
  const onModifyDealCTAClick = createChainedFunction(handleDealReceiptOverride, fireEditDealerOfferCTAClick);
  const enableSacaCreditApp = enableSaca && !isCashDeal && !isAccelerate && dealer?.sacaMmdId;
  if (isLoading && !isReceiptView) {
    return /*#__PURE__*/jsx(LoadingSpinner, {
      className: "margin-top-0"
    });
  }
  return /*#__PURE__*/jsxs(PanelWrapper, {
    isReceiptView: isReceiptView,
    children: [!isReceiptView && /*#__PURE__*/jsx(DealCardVehicleOverview, {
      dealData: dealDataWithDealer,
      indexClicked: indexClicked,
      onEmailDealerClick: onEmailDealerClick,
      onPhoneClick: onPhoneAndTextClick('phone'),
      onTextClick: onPhoneAndTextClick('text')
    }), /*#__PURE__*/jsxs("div", {
      className: clsx({
        'margin-vertical-2': !isReceiptView
      }),
      children: [/*#__PURE__*/jsx(DealCardOfferDetails, {
        creditApp: creditApp,
        dealId: dealId,
        offerDetails: deal,
        onSDPError: onSDPError,
        dealsDuck: dealsDuck,
        paymentsDuck: paymentsDuck,
        tradeIn: tradeIn,
        vehicle: dealDataWithDealer.vehicle,
        isAccelerate: isAccelerate,
        vdpUrl: getVDPUrl('nativeDealContainer'),
        isReceiptView: isReceiptView,
        onClick: handleWalletModalClose,
        handleDealReceiptOverride: handleDealReceiptOverride,
        isEsntialDeal: isEsntialDeal,
        onEmailCTAClick: isEcommerce ? onEmailCTAClick : null
      }), (!isEsntialDeal || isAccelerate) && !isEcommerce && /*#__PURE__*/
      // Show it only for DR and Non DR Deals
      jsxs(Fragment, {
        children: [isReceiptView && /*#__PURE__*/jsx("hr", {
          className: "margin-vertical-4"
        }), !isCashDeal && isAccelerate && renderTableRow('creditPreApproval', creditDecisionStatus, linkProps, onEmailDealerClick, vehicleProtectionProgram, isReceiptView), enableSacaCreditApp && renderTableRow('sacaApplication', isOwnerFinancingSubmitted && 'Submitted', {
          ...linkProps,
          handleSACAApplicationClick,
          handleSACAFollowUpClick,
          isVdp
        }, onEmailDealerClick, vehicleProtectionProgram, isReceiptView), renderTableRow('testDrive', testDriveStatus, {
          ...linkProps,
          testDriveStatus,
          onTestDriveMarkDoneClick,
          isAccelerate,
          vdpUrl: getVDPUrl('inquirySelection', '&inquiry=scheduleTestDrive'),
          onScheduleTestDriveClick
        }, null, vehicleProtectionProgram, isReceiptView), isAccelerate && /*#__PURE__*/jsxs(Fragment, {
          children: [isReceiptView && /*#__PURE__*/jsx("hr", {
            className: "margin-vertical-4"
          }), /*#__PURE__*/jsx(DealCardVehicleProtection, {
            ...sharedProps
          })]
        })]
      }), isReceiptView && isAccelerate && !isEcommerce && /*#__PURE__*/jsx(DealCardDeepLink, {
        ...linkProps,
        buttonText: "Modify Your Offer",
        buttonStyle: "primary",
        buttonClassName: "btn-block margin-top-5"
      }), isReceiptView && !isAccelerate && !isEsntialDeal && !isEcommerce && /*#__PURE__*/jsx(Button, {
        className: "margin-top-5",
        bsStyle: "primary",
        block: true,
        onClick: onModifyDealCTAClick,
        children: "Modify Your Offer"
      })]
    })]
  });
}
export { DealCard as default };
